import React from "react";
import {
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaLayerGroup,
  FaPen,
  FaTimes,
  FaUserTie,
  FaUsers,
} from "react-icons/fa";

import {
  formatCurrency,
  formatNumber,
  getColorClass,
  getStatusClass,
  getStatusLabel,
} from "../utils/groupesUtils";

function DetailItem({ label, value, icon: Icon }) {
  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        {Icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl bg-white text-slate-500 dark:bg-slate-950 dark:text-slate-300">
            <Icon />
          </span>
        )}

        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 break-words text-sm font-semibold text-slate-800 dark:text-slate-100">
            {value || "—"}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function GroupDetails({ group, onEdit, onClose }) {
  return (
    <div className="space-y-6">
      <section className="rounded-[2rem] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <div
            className={`grid h-20 w-20 shrink-0 place-items-center rounded-3xl text-2xl font-bold ${getColorClass(
              group.color_groupe
            )}`}
          >
            <FaLayerGroup />
          </div>

          <div className="min-w-0 flex-1">
            <h3 className="text-2xl font-bold text-slate-950 dark:text-white">
              {group.nom}
            </h3>

            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${getStatusClass(
                  group.is_active
                )}`}
              >
                {getStatusLabel(group.is_active)}
              </span>

              <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                Ordre {group.ordre_groupe || "—"}
              </span>

              <span className="rounded-full bg-slate-200 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                Quota {group.quota_groupe || "—"}
              </span>
            </div>
          </div>
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Horaires & publics
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem
            label="Horaire principal"
            value={group.horaire1}
            icon={FaClock}
          />
          <DetailItem
            label="Horaire secondaire"
            value={group.horaire2}
            icon={FaClock}
          />
          <DetailItem
            label="Années de naissance"
            value={group.annees_naissance}
            icon={FaCalendarAlt}
          />
          <DetailItem
            label="Quota"
            value={
              group.quota_groupe
                ? `${formatNumber(group.quota_groupe)} places`
                : "—"
            }
            icon={FaUsers}
          />
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Tarifs
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem
            label="Tarif licence"
            value={formatCurrency(group.tarif_licence)}
            icon={FaEuroSign}
          />
          <DetailItem
            label="Tarif cotisation"
            value={formatCurrency(group.tarif_cotisation)}
            icon={FaEuroSign}
          />
        </div>
      </section>

      <section>
        <h4 className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
          Encadrement
        </h4>

        <div className="grid gap-3 sm:grid-cols-2">
          <DetailItem
            label="Enseignant principal"
            value={group.enseignant1}
            icon={FaUserTie}
          />
          <DetailItem
            label="Enseignant secondaire"
            value={group.enseignant2}
            icon={FaUserTie}
          />
          <DetailItem
            label="Statut"
            value={getStatusLabel(group.is_active)}
            icon={FaCheckCircle}
          />
        </div>
      </section>

      <div className="sticky bottom-0 -mx-5 border-t border-slate-200 bg-white/90 px-5 py-4 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaTimes />
            Fermer
          </button>

          <button
            type="button"
            onClick={onEdit}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-bold text-white shadow-lg shadow-slate-950/20 transition hover:-translate-y-0.5 hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
          >
            <FaPen />
            Modifier
          </button>
        </div>
      </div>
    </div>
  );
}